import * as React from 'react';
import type { CallSortBy } from '@relay-towing/api';
import {
  BottomSheet,
  BottomSheetContent,
  RecordRow,
  SearchField,
  Stack,
} from '@relay-towing/ui';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { usePagedList } from '../../hooks/usePagedList';
import type { CallRecord } from '../calls/callRecord';
import { PagedRows } from './PagedRows';
import { firstLine } from '../status';
import { vehicleOf } from '../vehicle';

/** How a call reads where one is picked: its number, vehicle and customer. */
export const callChoiceLabel = (call: CallRecord): string =>
  [`#${call.dispatchNumber}`, vehicleOf(call.vehicle), call.contactName]
    .filter(Boolean)
    .join(' · ');

/**
 * Pick a call, newest first, searched on the server as the calls list is
 * (by number, customer, plate or VIN), for something made from a call: an
 * impound, an invoice. The server scopes the calls, and refuses one the
 * member may not use. Opened over the sheet that asked.
 */
export function CallPickerSheet({
  open,
  onOpenChange,
  onPick,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** False keeps the sheet open: the call cannot be used, and was told so. */
  onPick: (call: CallRecord) => boolean | void;
}) {
  const [search, setSearch] = React.useState('');
  const searchQuery = useDebouncedValue(search).trim() || undefined;
  const list = usePagedList<CallRecord>(
    'mobile-impound-calls',
    '/calls',
    { sortBy: 'date-desc' satisfies CallSortBy, searchQuery },
    { enabled: open }
  );
  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent title="Which call?" size="full">
        <Stack gap={4}>
          <SearchField
            value={search}
            onChange={setSearch}
            label="Search calls"
            placeholder="Number, customer, plate or VIN"
          />
          <PagedRows
            list={list}
            empty={searchQuery ? 'No calls match' : 'No calls yet'}
          >
            {(call) => (
              <RecordRow
                key={call.id}
                title={`#${call.dispatchNumber}`}
                meta={vehicleOf(call.vehicle) || 'Unspecified vehicle'}
                detail={[call.contactName, firstLine(call.pickupLocation)]
                  .filter(Boolean)
                  .join(' · ')}
                onClick={() => {
                  if (onPick(call) !== false) onOpenChange(false);
                }}
              />
            )}
          </PagedRows>
        </Stack>
      </BottomSheetContent>
    </BottomSheet>
  );
}
